import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { OptionsQuestion, QuestionProps } from '@/types/questionnaire';
import { Code, Eye, Filter, Hash, Shield, Tag, Type } from 'lucide-react';
import { OptionColumns } from './OptionColumns';
import { Badge } from '@/components/ui/badge';
import { MetadataCard } from './MetaDataCard';
import { EditQuestionModal } from './EditQuestionModal';
import { DeleteQuestionDialog } from './DeleteQuestionDialog';
import { router } from '@inertiajs/react';
import { useModal } from '@/contexts/ModalContext';

export function isOptionsQuestion(
  question: QuestionProps
): question is OptionsQuestion {
  return ['radio', 'select', 'multi_select'].includes(question.question_type);
}
export function QuestionDetails({
  sectionQuestions,
  templateId,
  questionDetails,
  isLoading,
}: {
  sectionQuestions?: Array<{ id: number; key: string; order: number }>;
  questionDetails?: QuestionProps;
  isLoading: boolean;
  templateId: number;
}) {
  const { openModal, closeModal } = useModal();

  const handleDeleteSuccess = () => {
    const fallbackRoute = `/admin/questionnaire_templates/${templateId}/questionnaire_sections?section_id=${questionDetails?.questionnaire_section_id}`;
    if (!sectionQuestions || !questionDetails || sectionQuestions.length <= 1) {
      router.get(fallbackRoute);
      return;
    }
    const currentIndex = sectionQuestions.findIndex(
      (q) => q.id === questionDetails.id
    );
    const nextQuestion =
      sectionQuestions[currentIndex + 1] || sectionQuestions[currentIndex - 1];
    if (nextQuestion)
      router.get(
        `/admin/questionnaire_sections/${questionDetails.questionnaire_section_id}/questions?question_id=${nextQuestion.id}`
      );
    else router.get(fallbackRoute);
  };

  if (isLoading) return <div>Loading...</div>;
  if (!questionDetails) return 'Select a question to view details';
  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="flex justify-between items-start">
        <h1 className="text-xl font-bold">{questionDetails.label}</h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const modalId = openModal({
                component: (
                  <EditQuestionModal
                    question={questionDetails}
                    allQuestions={
                      (sectionQuestions as QuestionProps[]) || [questionDetails]
                    }
                    onClose={() => closeModal(modalId)}
                  />
                ),
                heading: 'Edit Question',
              });
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              const modalId = openModal({
                component: (
                  <DeleteQuestionDialog
                    question={questionDetails}
                    onClose={() => closeModal(modalId)}
                    onSuccess={handleDeleteSuccess}
                  />
                ),
                heading: 'Delete Question',
              });
            }}
          >
            Delete
          </Button>
        </div>
      </div>
      <div className=" mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
          <MetadataCard icon={Code} iconColor="text-blue-500" label="Key">
            <code className="text-sm">{questionDetails.key}</code>
          </MetadataCard>

          <MetadataCard icon={Tag} iconColor="text-purple-500" label="Type">
            <Badge variant="outline">{questionDetails.question_type}</Badge>
          </MetadataCard>

          <MetadataCard
            icon={Shield}
            iconColor="text-green-500"
            label="Required"
          >
            <span className="text-sm">
              {questionDetails.required ? 'Yes' : 'No'}
            </span>
          </MetadataCard>

          <MetadataCard
            icon={Hash}
            iconColor="text-orange-500"
            label="Position"
          >
            <span className="text-sm font-medium">
              #{questionDetails.order}
            </span>
          </MetadataCard>

          <MetadataCard
            icon={Filter}
            iconColor="text-indigo-500"
            label="Can be Filter"
          >
            {questionDetails.is_filterable ? (
              <Badge>Yes</Badge>
            ) : (
              <Badge variant="secondary">No</Badge>
            )}
          </MetadataCard>

          <MetadataCard
            icon={Eye}
            iconColor="text-cyan-500"
            label="Mentor Visibility"
          >
            {questionDetails.is_visible_to_mentors !== false ? (
              <Badge>Visible</Badge>
            ) : (
              <Badge variant="destructive">Hidden</Badge>
            )}
          </MetadataCard>

          {questionDetails.filter_display_name && (
            <div className="md:col-span-2">
              <MetadataCard
                icon={Type}
                iconColor="text-amber-500"
                label="Custom Filter Label"
              >
                <code className="text-sm">
                  {questionDetails.filter_display_name}
                </code>
              </MetadataCard>
            </div>
          )}
        </div>
      </div>
      {isOptionsQuestion(questionDetails) &&
        questionDetails?.options?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">
              Options ({questionDetails.options.length})
            </h3>
            <DataTable
              data={questionDetails.options.map((option, index) => ({
                id: index,
                ...option,
              }))}
              columns={OptionColumns()}
            />
          </div>
        )}
    </div>
  );
}
